import { Reducer } from 'redux'
import { string } from 'yup'

export interface RiskCounts {
  UNANSWERED: number
  HIGH: number
  MEDIUM: number
  NONE: number
  NOT_APPLICABLE: number
}

export type PillarKeys =
  | 'operationalExcellence'
  | 'security'
  | 'reliability'
  | 'performanceEfficiency'
  | 'costOptimization'
  | 'sustainability'

export interface Filter {
  pillarId?: string[]
  status?: string[]
  questionId?: string[]
  bestPracticeId?: string[]
  awsAccountId?: string[]
  region?: string[]
}

export interface Findings {
  id: string
  assessmentId: string
  bestPracticeId: string
  checkId: string
  checkName: string
  description: string
  pillarId: string
  questionId: string
  region: string
  status: boolean | string
  awsAccountId: string
  questionCode: string
  accountName: string
  checkTitle: string
  resourceName: string
  resourceId: string
}

export interface PillarReviewSummary {
  pillarId?: string
  pillarName: string
  riskCounts: RiskCounts
}

export interface BestPractice {
  totalChecks: string
  passedChecks: string
  failedChecks: string
  riskLevel: string
}

export interface QuestionDetails {
  pillar: string
  totalChecks: string
  passedChecks: string
  failedChecks: string
  riskLevel: string
  bestPractices: Record<string, BestPractice>
}

export interface ChecksSummary {
  assessmentId: string
  totalChecks: string
  passedChecks: string
  failedChecks: string
  pillars: Record<PillarKeys, string>
  totalFailedFindings?: number
  questions?: Record<string, QuestionDetails>
}

export interface LensReview {
  lensAlias: string
  lensArn: string
  lensVersion: string
  lensName: string
  lensStatus: string
  pillarReviewSummaries: PillarReviewSummary[]
  updatedAt: string
  riskCounts: RiskCounts
}

export enum PillarWorkloadActionType {
  SET_WORKLOAD_DETAILS = 'SET_WORKLOAD_DETAILS',
  CLEAR_WORKLOAD_DETAILS = 'CLEAR_WORKLOAD_DETAILS',
  SET_CHECKS_FINDINGS = 'SET_CHECKS_FINDINGS',
  CLEAR_CHECKS_FINDINGS = 'CLEAR_CHECKS_FINDINGS',
  CLEAR_FINDINGS = 'CLEAR_FINDINGS',
  SET_FINDINGS = 'SET_FINDINGS',
  SET_FILTER = 'SET_FILTER',
  CLEAR_FILTER = 'CLEAR_FILTER',
  SET_TOKEN = 'SET_TOKEN',
  SET_TOTAL = 'SET_TOTAL',
  CLEAR_TOTAL = 'CLEAR_TOTAL',
}

export interface PillarWorkloadDetails {
  workloadId: string
  lensReview: LensReview
}

export interface PillarWorkloadState {
  pillarWorkloadDetails: PillarWorkloadDetails | null
  checksSummary: ChecksSummary | null
  findings: Findings[]
  nextToken: string
  totalFindings: number
  filter: Filter | null
}

const initialState: PillarWorkloadState = {
  pillarWorkloadDetails: null,
  checksSummary: null,
  findings: [],
  nextToken: '',
  totalFindings: 0,
  filter: {},
}

type actionType =
  | {
      type: PillarWorkloadActionType.SET_WORKLOAD_DETAILS
      payload: PillarWorkloadDetails
    }
  | { type: PillarWorkloadActionType.CLEAR_WORKLOAD_DETAILS }
  | {
      type: PillarWorkloadActionType.SET_CHECKS_FINDINGS
      payload: ChecksSummary
    }
  | {
      type: PillarWorkloadActionType.CLEAR_FINDINGS
    }
  | {
      type: PillarWorkloadActionType.SET_FINDINGS
      payload: {
        findings: Findings[]
        paginationToken?: string
      }
    }
  | {
      type: PillarWorkloadActionType.SET_FILTER
      payload: Filter
    }
  | {
      type: PillarWorkloadActionType.CLEAR_FILTER
    }
  | {
      type: PillarWorkloadActionType.SET_TOKEN
      payload: string
    }
  | {
      type: PillarWorkloadActionType.CLEAR_CHECKS_FINDINGS
    }
  | {
      type: PillarWorkloadActionType.SET_TOTAL
      payload: number
    }
  | {
      type: PillarWorkloadActionType.CLEAR_TOTAL
    }

export const reducer: Reducer<PillarWorkloadState, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case PillarWorkloadActionType.SET_WORKLOAD_DETAILS:
      return { ...state, pillarWorkloadDetails: action.payload }
    case PillarWorkloadActionType.CLEAR_WORKLOAD_DETAILS:
      return { ...state, pillarWorkloadDetails: null }
    case PillarWorkloadActionType.SET_CHECKS_FINDINGS:
      return {
        ...state,
        checksSummary: action.payload,
      }
    case PillarWorkloadActionType.SET_FINDINGS:
      return {
        ...state,
        findings: action.payload.paginationToken
          ? [...state.findings, ...action.payload.findings]
          : action.payload.findings,
      }
    case PillarWorkloadActionType.SET_FILTER:
      return {
        ...state,
        filter: action.payload,
      }
    case PillarWorkloadActionType.CLEAR_FILTER:
      return {
        ...state,
        filter: {},
      }
    case PillarWorkloadActionType.SET_TOKEN:
      return {
        ...state,
        nextToken: action.payload,
      }
    case PillarWorkloadActionType.CLEAR_FINDINGS:
      return {
        ...state,
        findings: [],
      }
    case PillarWorkloadActionType.CLEAR_CHECKS_FINDINGS:
      return {
        ...state,
        checksSummary: null,
      }
    case PillarWorkloadActionType.SET_TOTAL:
      return {
        ...state,
        totalFindings: action.payload,
      }
    case PillarWorkloadActionType.CLEAR_TOTAL:
      return {
        ...state,
        totalFindings: 0,
      }

    default:
      return state
  }
}
